import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import anime from 'animejs';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useGlobalModal, ToastMessage } from '../../context/GlobalModalContext';

const toastIcons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const toastStyles = {
  success: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    icon: 'text-emerald-400',
    title: 'text-emerald-300',
  },
  error: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    icon: 'text-red-400',
    title: 'text-red-300',
  },
  info: {
    bg: 'bg-primary-500/10',
    border: 'border-primary-500/20',
    icon: 'text-primary-400',
    title: 'text-primary-300',
  },
  warning: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    icon: 'text-amber-400',
    title: 'text-amber-300',
  },
};

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const Icon = toastIcons[toast.type];
  const styles = toastStyles[toast.type];

  useEffect(() => {
    if (ref.current) {
      // Entrance animation
      anime({
        targets: ref.current,
        translateX: [100, 0],
        opacity: [0, 1],
        scale: [0.9, 1],
        duration: 400,
        easing: 'easeOutExpo',
      });
    }
  }, []);

  const handleDismiss = () => {
    if (ref.current) {
      anime({
        targets: ref.current,
        translateX: [0, 100],
        opacity: [1, 0],
        scale: [1, 0.9],
        duration: 300,
        easing: 'easeInQuad',
        complete: onDismiss,
      });
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex items-start gap-3 p-4 rounded-xl border backdrop-blur-xl',
        'shadow-lg shadow-black/20',
        'max-w-sm w-full',
        styles.bg,
        styles.border
      )}
      style={{ opacity: 0 }}
    >
      {/* Subtle gradient glow */}
      <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
        <div
          className={cn(
            'absolute -top-12 -right-12 w-24 h-24 rounded-full blur-2xl opacity-30',
            toast.type === 'success' && 'bg-emerald-500',
            toast.type === 'error' && 'bg-red-500',
            toast.type === 'info' && 'bg-primary-500',
            toast.type === 'warning' && 'bg-amber-500'
          )}
        />
      </div>

      <div className={cn('flex-shrink-0 mt-0.5', styles.icon)}>
        <Icon className="w-5 h-5" />
      </div>

      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium', styles.title)}>{toast.title}</p>
        {toast.description && (
          <p className="mt-1 text-sm text-slate-400">{toast.description}</p>
        )}
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className={cn(
              'mt-2 text-sm font-medium transition-colors',
              styles.title,
              'hover:underline underline-offset-2'
            )}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 rounded-lg text-slate-500 hover:text-white hover:bg-white/[0.06] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts, dismissToast } = useGlobalModal();

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={() => dismissToast(toast.id)} />
        </div>
      ))}
    </div>,
    document.body
  );
}
